import { ApprovalStatus } from '@agentic/contracts';
import {
  buttonClass,
  Countdown,
  EmptyState,
  formatDateTime,
  formatRelative,
  IdText,
  Segmented,
  SkeletonRows,
  StatGrid,
  StatTile,
  segmentClass,
  Table,
} from '@agentic/ui-kit';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ApprovalStatusChip } from '../../../app/chips.tsx';
import { approvalStatusLabel, shortTitle } from '../../../app/presentation.ts';
import { approvalsQuery, taskQuery } from '../../../app/queries.ts';
import { QueryError } from '../../../app/route-states.tsx';
import { CARD, PAGE, PageHeader } from '../../../app/shell.tsx';
import { useMe } from '../../../app/use-me.ts';
import { ProcessApprovalRequests } from '../../../features/approval-requests/inbox.tsx';

const Filter = z.union([ApprovalStatus, z.literal('all')]);
/** `requests`: process approval requests filter (17e), default `mine`. */
const Search = z.object({
  status: Filter.optional().catch(undefined),
  requests: z.enum(['mine', 'all']).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/approvals/')({
  validateSearch: Search,
  component: ApprovalsPage,
});

const FILTERS: z.infer<typeof Filter>[] = [
  'pending',
  'approved',
  'rejected',
  'invalidated',
  'expired',
  'all',
];

function ApprovalsPage() {
  const { status = 'pending', requests = 'mine' } = Route.useSearch();
  const navigate = useNavigate({ from: '/approvals/' });
  const query = useQuery(approvalsQuery(status === 'all' ? undefined : status));
  const me = useMe();
  const items = query.data?.items ?? [];
  // Pending requests drive the count tiles whatever the filter; the shell keeps this query warm for its badge.
  const pending = useQuery({ ...approvalsQuery('pending'), meta: { silent: true } });
  const pendingItems = pending.data?.items ?? [];
  const soonest = [...pendingItems].sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt))[0];
  const boundActions = pendingItems.reduce((n, a) => n + a.actionIds.length, 0);
  // Episode records of the listed approvals (cached) for the titles, the action titles and the case link (20a).
  const taskIds = [...new Set(items.map((a) => a.taskId))].slice(0, 50);
  const viewQueries = useQueries({ queries: taskIds.map((id) => ({ ...taskQuery(id), staleTime: 30_000 })) });
  const views = new Map(
    viewQueries.flatMap((q, i) => (q.data && taskIds[i] ? [[taskIds[i], q.data] as const] : [])),
  );

  return (
    <>
      <PageHeader
        title="Approvals"
        description="Each request covers exact changes and expires. Review the changes before you decide; if they change, a new review is needed."
      >
        <Segmented aria-label="Approval status" className="mt-5">
          {FILTERS.map((f) => (
            <Link
              key={f}
              to="/approvals"
              search={(prev) => ({ ...prev, status: f === 'pending' ? undefined : f })}
              replace
              className={segmentClass('wb-btn')}
              // Exact: the default filter has no `status` in the URL, so a partial search match would keep it active everywhere.
              activeOptions={{ exact: true }}
              activeProps={{ 'aria-current': 'page' }}
            >
              {f === 'all' ? 'All' : approvalStatusLabel(f)}
            </Link>
          ))}
        </Segmented>
      </PageHeader>
      <div className={PAGE}>
        <StatGrid aria-label="Approval counts" role="group">
          <StatTile
            surface="white"
            tone="accent"
            label="Pending"
            value={pending.isPending ? '–' : pendingItems.length}
            hint="Waiting for a decision"
          />
          <StatTile
            surface="white"
            label="Soonest expiry"
            size="md"
            value={soonest ? formatRelative(soonest.expiresAt) : '—'}
            hint={soonest ? formatDateTime(soonest.expiresAt) : 'Nothing pending'}
          />
          <StatTile
            surface="white"
            label="Bound actions"
            value={pending.isPending ? '–' : boundActions}
            hint="Exact arguments, target versions, expiry"
          />
        </StatGrid>
        <ProcessApprovalRequests
          filter={requests}
          onFilter={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, requests: next === 'mine' ? undefined : next }),
              replace: true,
            })
          }
        />
        <div className={`overflow-hidden ${CARD}`}>
          {query.isPending ? (
            <SkeletonRows rows={5} label="Loading approvals" />
          ) : query.isError ? (
            <div className="p-4">
              <QueryError error={query.error} onRetry={() => void query.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title={
                status === 'pending'
                  ? 'Inbox clear'
                  : `No approvals with status “${status === 'all' ? 'All' : approvalStatusLabel(status)}”`
              }
            >
              {status === 'pending'
                ? 'Approval requests appear here when an agent proposes an action that needs a person to approve it.'
                : 'Try another status.'}
            </EmptyState>
          ) : (
            <Table aria-label="Approvals" data-testid="approvals-table">
              <thead>
                <tr>
                  <th scope="col" className="min-w-[14rem]">
                    Case
                  </th>
                  <th scope="col" className="min-w-[12rem]">
                    Proposed effect
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Requested by</th>
                  <th scope="col">
                    <span className="sr-only">Review</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const view = views.get(a.taskId);
                  const task = view?.task;
                  const caseId = view?.caseId ?? task?.caseId;
                  const activation = view?.activationId ?? task?.activationId;
                  const titles = a.actionIds.map((id) => {
                    const act = view?.proposal?.actions.find((x) => x.actionId === id);
                    return act?.title ?? act?.tool;
                  });
                  const first = titles[0];
                  const refId = `appr-${a.approvalId}`;
                  return (
                    <tr
                      key={a.approvalId}
                      data-interactive
                      data-approval-id={a.approvalId}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a,button')) return;
                        void navigate({ to: '/approvals/$approvalId', params: { approvalId: a.approvalId } });
                      }}
                    >
                      <td className="min-w-[14rem] max-w-[26rem]">
                        <Link
                          id={refId}
                          to="/approvals/$approvalId"
                          params={{ approvalId: a.approvalId }}
                          title={task?.objective}
                          className="line-clamp-2 text-[14px] font-medium text-ink hover:underline"
                        >
                          {task ? shortTitle(task) : 'Approval request'}
                        </Link>
                        <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-ink-3">
                          <span className="font-mono">{task?.capability}</span>
                          <span title="Policy bundles">{a.policyContext.bundles.join(', ')}</span>
                          <IdText value={a.approvalId} length={8} className="text-ink-3" />
                          {caseId && (
                            <Link
                              to="/cases/$caseId"
                              params={{ caseId }}
                              search={activation ? { activity: activation } : {}}
                              className="underline"
                              data-testid="approval-case-link"
                            >
                              Open the case
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="min-w-[12rem] text-[13px] text-ink">
                        {first ? (
                          <>
                            {first}
                            {a.actionIds.length > 1 && (
                              <span className="text-ink-2"> and {a.actionIds.length - 1} more</span>
                            )}
                          </>
                        ) : (
                          <span className="text-ink-2">
                            {a.actionIds.length} action{a.actionIds.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td>
                        <ApprovalStatusChip value={a.status} variant="text" />
                      </td>
                      <td className="whitespace-nowrap text-ink-2" title={formatDateTime(a.expiresAt)}>
                        {a.status === 'pending' ? (
                          <>
                            in <Countdown until={a.expiresAt} />
                          </>
                        ) : (
                          formatRelative(a.expiresAt)
                        )}
                        <div className="text-[12px] text-ink-3">{formatDateTime(a.expiresAt)}</div>
                      </td>
                      <td className="text-[13px] text-ink-2">
                        {task ? (
                          task.requestedBy === me.id ? (
                            <span title={task.requestedBy}>You</span>
                          ) : (
                            <code className="font-mono text-[12.5px]" title={task.requestedBy}>
                              {task.requestedBy.length > 18
                                ? `${task.requestedBy.slice(0, 8)}…`
                                : task.requestedBy}
                            </code>
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <Link
                          to="/approvals/$approvalId"
                          params={{ approvalId: a.approvalId }}
                          aria-describedby={refId}
                          className={buttonClass(a.status === 'pending' ? 'secondary' : 'ghost', 'sm')}
                        >
                          {a.status === 'pending' ? 'Review' : 'View'}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
}
